import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isApiError } from '@shared/api/errors';
import {
  Alert,
  AlertTitle,
  Button,
  Heading,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from '@shared/ui';
import {
  helpdeskDeletionRefreshMs,
  runtimeThreadQuery,
  useHelpdeskRuntime,
  type PresentationThread as HelpdeskThread,
  type PresentationTask as HelpdeskTask,
  type PresentationMessage,
} from '../model/runtime';

import { HelpdeskReplyComposerView as HelpdeskReplyComposer } from './HelpdeskReplyComposerView';
import type { HelpdeskEmailChannel } from '../api/email-delivery';
import { HelpdeskThreadMessage } from './HelpdeskThreadMessage';

export interface HelpdeskConversationViewProps {
  taskUuid: string;
  emailChannel?: HelpdeskEmailChannel | null;
  viewerAccessScope: string;
  canCreate: boolean;
  viewerProfileId?: string;
  active?: boolean;
  page?: number;
  pageSize?: number;
  onPaginationChange?: (page: number, pageSize: number) => void;
  query?: UseQueryResult<HelpdeskThread>;
  renderMessageAction?: (
    message: PresentationMessage,
    refresh: () => void,
    masterDeletionState: HelpdeskTask['deletionState']
  ) => ReactNode;
}

/** One authorized thread response owns the original, reply page and exact reply totals. */
export function HelpdeskConversationView({
  taskUuid,
  emailChannel,
  viewerAccessScope,
  canCreate,
  viewerProfileId,
  active = true,
  page: controlledPage,
  onPaginationChange,
  query,
  renderMessageAction,
}: HelpdeskConversationViewProps) {
  const { t } = useTranslation();
  const runtime = useHelpdeskRuntime();
  const client = useQueryClient();
  const scope = `${viewerAccessScope}:${taskUuid}`;
  const activeScope = useRef(scope);
  activeScope.current = scope;
  const refreshGeneration = useRef(0);
  const refreshActive = useRef(active);
  refreshActive.current = active;
  useEffect(() => {
    refreshActive.current = active;
    refreshGeneration.current += 1;
    return () => {
      refreshActive.current = false;
      refreshGeneration.current += 1;
    };
  }, [active, scope]);
  const [localBatch, setLocalBatch] = useState({ scope, page: 1 });

  const [restartScope, setRestartScope] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<{ scope: string; error: unknown } | null>(null);
  const [composer, setComposer] = useState<'support-reply' | 'internal-comment'>('support-reply');
  const page = controlledPage ?? (localBatch.scope === scope ? localBatch.page : 1);
  const pageSize = 20;
  const [loaded, setLoaded] = useState<{ scope: string; pages: Record<number, HelpdeskThread> }>({
    scope,
    pages: {},
  });
  const ownQuery = useQuery({
    ...runtimeThreadQuery(runtime, viewerAccessScope, taskUuid, { page, pageSize }),
    enabled: active && !query,
    placeholderData: (previous, previousQuery) =>
      previous?.task.id === taskUuid && previousQuery?.queryKey[1] === viewerAccessScope
        ? previous
        : undefined,
  });
  const thread = query ?? ownQuery;
  const acceptedKey = ['helpdesk', viewerAccessScope, 'pending-thread-replies', taskUuid] as const;
  const accepted = useQuery({
    queryKey: acceptedKey,
    queryFn: () => Promise.resolve<HelpdeskThread['items']>([]),
    initialData: [],
    enabled: false,
  });
  useEffect(() => {
    if (thread.isError) {
      if (isApiError(thread.error) && [401, 403, 404].includes(thread.error.status))
        setLoaded({ scope, pages: {} });
      return;
    }
    if (!thread.data) return;
    if (restartScope === scope) {
      if (page !== 1 || thread.isFetching || thread.isPlaceholderData) return;
      setRestartScope(null);
      setRefreshError(null);
    }
    if (thread.isPlaceholderData) return;
    const response = thread.data;
    setLoaded((current) => ({
      scope,
      pages: { ...(current.scope === scope ? current.pages : {}), [response.meta.page]: response },
    }));
    const persisted = new Set(thread.data.items.map((item) => item.id));
    client.setQueryData<HelpdeskThread['items']>(
      ['helpdesk', viewerAccessScope, 'pending-thread-replies', taskUuid],
      (current = []) => current.filter((item) => !persisted.has(item.id))
    );
  }, [
    client,
    taskUuid,
    thread.data,
    thread.isError,
    thread.error,
    thread.isFetching,
    restartScope,
    page,
    thread.isPlaceholderData,
    viewerAccessScope,
    scope,
  ]);
  const refreshPending = useRef<() => Promise<void>>(async () => {});
  const changePage = (nextPage: number) => {
    setLocalBatch({ scope, page: nextPage });
    onPaginationChange?.(nextPage, 20);
  };
  const refreshLoaded = async () => {
    const generation = refreshGeneration.current;
    const isCurrent = () =>
      refreshActive.current &&
      refreshGeneration.current === generation &&
      activeScope.current === scope;
    if (!isCurrent()) return;
    const error = thread.error ?? (refreshError?.scope === scope ? refreshError.error : null);
    if (isApiError(error) && [401, 403, 404].includes(error.status) && page !== 1) {
      setRestartScope(scope);
      changePage(1);
      return;
    }
    const result = await thread.refetch({ cancelRefetch: true });
    if (result.isError || !isCurrent()) return;
    setRefreshError(null);
    const earlierPages = Object.keys(loaded.scope === scope ? loaded.pages : {})
      .map(Number)
      .filter((number) => number !== page);
    for (const number of earlierPages) {
      if (!isCurrent()) return;
      try {
        const response = await client.fetchQuery({
          ...runtimeThreadQuery(runtime, viewerAccessScope, taskUuid, {
            page: number,
            pageSize: 20,
          }),
          staleTime: 0,
        });
        if (!isCurrent()) return;
        setLoaded((current) =>
          current.scope === scope
            ? { scope, pages: { ...current.pages, [number]: response } }
            : current
        );
      } catch (error) {
        if (!isCurrent()) return;
        if (isApiError(error) && [401, 403, 404].includes(error.status))
          setLoaded((current) => (current.scope === scope ? { scope, pages: {} } : current));
        setRefreshError({ scope, error });
        break;
      }
    }
  };
  const saved = (task: HelpdeskTask, kind: 'support-reply' | 'internal-comment' | 'user-reply') => {
    client.setQueryData<HelpdeskThread['items']>(acceptedKey, (current = []) => [
      ...current.filter((item) => item.id !== task.id),
      { ...task, parentId: taskUuid, kind, signature: task.author?.displayName ?? null, files: [] },
    ]);
    void refreshLoaded();
  };
  const readError = thread.error ?? (refreshError?.scope === scope ? refreshError.error : null);
  const denied = isApiError(readError) && [401, 403, 404].includes(readError.status);
  const errorTitle =
    isApiError(readError) && readError.status === 403
      ? t('helpdesk.denied', 'Helpdesk access required')
      : isApiError(readError) && readError.status === 404
        ? t('helpdesk.notFound', 'Request not found or unavailable')
        : t('common.error', 'Something went wrong');
  const currentData = thread.isError || thread.isPlaceholderData ? undefined : thread.data;
  const pages = loaded.scope === scope ? loaded.pages : {};
  const data = denied || restartScope === scope ? undefined : (currentData ?? pages[1]);
  const combined = Object.entries(pages)
    .sort(([a], [b]) => Number(a) - Number(b))
    .flatMap(([, response]) => response.items);
  const byId = new Map(combined.map((item) => [item.id, item]));
  for (const item of currentData?.items ?? []) byId.set(item.id, item);
  const items = [...byId.values()].sort((a, b) => {
    if (a.createdAt === null && b.createdAt !== null) return 1;
    if (a.createdAt !== null && b.createdAt === null) return -1;
    return (a.createdAt ?? '').localeCompare(b.createdAt ?? '') || a.id.localeCompare(b.id);
  });
  const meta = currentData?.meta ?? data?.meta;
  const successfulPages = new Set(Object.keys(pages).map(Number));
  if (currentData) successfulPages.add(currentData.meta.page);
  let lastContiguousPage = 0;
  while (successfulPages.has(lastContiguousPage + 1)) lastContiguousPage += 1;
  const pending = accepted.data.filter((item) => !byId.has(item.id));
  refreshPending.current = refreshLoaded;
  const deliveryUnresolved = [...items, ...pending].some(
    (item) => item.emailDelivery?.status === 'pending' || item.emailDelivery?.status === 'ambiguous'
  );
  const deletionPending =
    data?.task.deletionState?.status === 'pending' ||
    items.some((item) => item.deletionState?.status === 'pending');
  useEffect(() => {
    if (
      !active ||
      runtime.audience !== 'staff' ||
      !(deletionPending || deliveryUnresolved) ||
      readError
    )
      return;
    let refreshing = false;
    const timer = setInterval(() => {
      if (refreshing) return;
      refreshing = true;
      void refreshPending.current().finally(() => {
        refreshing = false;
      });
    }, helpdeskDeletionRefreshMs);
    return () => {
      clearInterval(timer);
    };
  }, [active, runtime.audience, deletionPending, deliveryUnresolved, readError, scope]);
  const replyComposer = (
    <HelpdeskReplyComposer
      viewerAccessScope={viewerAccessScope}
      taskUuid={taskUuid}
      emailChannel={emailChannel}
      showAcceptedReplies={false}
      activeMode={composer}
      active={active && Boolean(data) && !readError}
      onSaved={(reply) => {
        saved(reply, runtime.audience === 'requester' ? 'user-reply' : 'support-reply');
      }}
    />
  );
  return (
    <section className="space-y-4" aria-label={t('helpdesk.commands.conversation', 'Conversation')}>
      {readError ? (
        <Alert variant="destructive">
          <AlertTitle>{errorTitle}</AlertTitle>
          <Button
            onClick={() => {
              void refreshLoaded();
            }}
          >
            {t('common.retry', 'Retry')}
          </Button>
        </Alert>
      ) : null}
      {!data || !meta ? (
        !readError ? (
          <Text role="status">{t('common.loading', 'Loading...')}</Text>
        ) : null
      ) : (
        <>
          <HelpdeskThreadMessage
            taskUuid={taskUuid}
            viewerProfileId={viewerProfileId}
            message={data.task}
            original
            action={renderMessageAction?.(
              data.task,
              () => {
                void refreshLoaded();
              },
              data.task.deletionState
            )}
          />
          <section
            className="space-y-3 border-t border-border pt-4"
            aria-label={t('helpdesk.thread.replies', 'Replies')}
          >
            <Heading as="h2" size="sm">
              {t('helpdesk.thread.replies', 'Replies')}
            </Heading>
            {items.length === 0 ? (
              <Text color="muted">{t('helpdesk.thread.empty', 'No replies yet.')}</Text>
            ) : null}
            {items.map((message) => (
              <HelpdeskThreadMessage
                key={message.id}
                taskUuid={taskUuid}
                viewerProfileId={viewerProfileId}
                message={message}
                own={runtime.audience === 'requester' ? message.kind === 'user-reply' : undefined}
                action={renderMessageAction?.(
                  message,
                  () => {
                    void refreshLoaded();
                  },
                  data.task.deletionState
                )}
              />
            ))}
            {pending.map((message) => (
              <HelpdeskThreadMessage
                key={message.id}
                taskUuid={taskUuid}
                viewerProfileId={viewerProfileId}
                message={message}
                own={runtime.audience === 'requester' ? message.kind === 'user-reply' : undefined}
                action={renderMessageAction?.(
                  message,
                  () => {
                    void refreshLoaded();
                  },
                  data.task.deletionState
                )}
              />
            ))}
            {lastContiguousPage < meta.totalPages ? (
              <Button
                variant="outline"
                disabled={thread.isFetching || Boolean(readError)}
                onClick={() => {
                  changePage(lastContiguousPage + 1);
                }}
              >
                {thread.isFetching
                  ? t('common.loading', 'Loading...')
                  : t('helpdesk.thread.loadMore', 'Load more')}
              </Button>
            ) : null}
          </section>
        </>
      )}
      <div hidden={!data} className="border-t border-border pt-4">
        {canCreate ? (
          <section className="space-y-3">
            <Heading as="h2" size="lg">
              {t('helpdesk.thread.write', 'Write a message')}
            </Heading>
            {runtime.audience === 'staff' ? (
              <Tabs
                value={composer}
                onValueChange={(value) => {
                  if (value === 'support-reply' || value === 'internal-comment') setComposer(value);
                }}
              >
                <TabsList
                  variant="secondary"
                  aria-label={t('helpdesk.thread.write', 'Write a message')}
                >
                  <TabsTrigger value="support-reply" variant="secondary">
                    {t('helpdesk.commands.reply', 'Reply')}
                  </TabsTrigger>
                  <TabsTrigger value="internal-comment" variant="secondary">
                    {t('helpdesk.thread.internalComment', 'Internal comment')}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="support-reply" forceMount hidden={composer !== 'support-reply'}>
                  {replyComposer}
                </TabsContent>
                <TabsContent
                  value="internal-comment"
                  forceMount
                  hidden={composer !== 'internal-comment'}
                >
                  <HelpdeskReplyComposer
                    viewerAccessScope={viewerAccessScope}
                    taskUuid={taskUuid}
                    kind="internal-comment"
                    showAcceptedReplies={false}
                    onSaved={(reply) => {
                      saved(reply, 'internal-comment');
                    }}
                  />
                </TabsContent>
              </Tabs>
            ) : (
              replyComposer
            )}
          </section>
        ) : null}
      </div>
    </section>
  );
}
